import { MaybeRef, toValue } from 'vue';

import useValidation, { ValidationRule } from '../common/composables/useValidation';
import { JsDays } from '../constants/dates';
import { DateDirection, DaysMode } from '../constants/field-types';
import { IDateRestriction } from '../declarations/fields';

const { EarlierThen, EarlierThenOrExact, LaterThenOrExact, LaterThen } = useValidation();

/**
 * "Склеиваем" правила через 'ИЛИ' или 'И'
 * @param {Array<ValidationRule>} rules
 * @param {'and' | 'or'} joinType
 * @returns {ValidationRule | null}
 */
export function joinRules(
  rules: Array<ValidationRule>,
  joinType: 'and' | 'or'
): null | ValidationRule {
  if (rules.length > 0) {
    const rulesCount = rules.length;
    const fails: Array<string> = [];

    return (value: string | null): true | string => {
      for (let i = 0; i < rulesCount; i += 1) {
        const valid = rules[i](value);
        if (typeof valid === 'string') {
          fails.push(valid);
        }
      }
      if (joinType === 'and' && fails.length > 0) {
        return fails.join(' и ');
      }
      if (joinType === 'or' && fails.length === rulesCount) {
        return fails.join(' или ');
      }
      return true;
    };
  }
  return null;
}

/**
 * Вычисляем количество реальных дней, необходимы для создания ограничения.
 * Если календарные дни, то возвращается это же количество дней, если про
 * рабочие — исключаются ТОЛЬКО субботы и воскресенья.
 *
 * @param {IDateRestriction} restriction
 * @param anchorDate
 * @param {boolean} backward - направление отсчета времени, если включен, то отсчет будет идти "назад"
 * @returns {number}
 */
export function countDaysDiff(
  restriction: IDateRestriction,
  anchorDate: MaybeRef<Date>,
  backward: boolean = false
): number {
  if (restriction.daysMode === DaysMode.workdays) {
    const anchor = toValue(anchorDate);
    const diffDate = new Date(anchor);
    let diff = restriction.days as number;
    let currentWeekday = anchor.getDay();
    while (diff > 0) {
      if (backward) {
        diffDate.setDate(diffDate.getDate() - 1);
      } else {
        diffDate.setDate(diffDate.getDate() + 1);
      }
      currentWeekday = diffDate.getDay();
      if (![JsDays.SUNDAY, JsDays.SATURDAY].includes(currentWeekday)) {
        diff -= 1;
      }
    }
    const daysDiff = Math.abs(diffDate.getTime() - anchor.getTime()) / (1000 * 60 * 60 * 24);
    // Если речь идет о прошлом, то там необходимо округлить дни вверх.
    return backward ? Math.floor(daysDiff) : Math.ceil(daysDiff);
  }
  return restriction.days as number;
}

/**
 * Получаем правило валидации для минимальных ограничений, относительно даты отсчета
 *
 * @param {IDateRestriction} past
 * @param anchorDate
 * @returns {ValidationRule}
 */
export function getPastRelativeRestrictionRule(
  past: IDateRestriction,
  anchorDate: MaybeRef<Date>
): ValidationRule {
  const days = countDaysDiff(past, anchorDate, true);
  const min = new Date(toValue(anchorDate));
  min.setDate(min.getDate() - days);
  if (past.direction === DateDirection.before) {
    return LaterThenOrExact(min.toLocaleDateString());
  }
  return EarlierThen(min.toLocaleDateString());
}
/**
 * Получаем правило валидации для максимальных ограничений, относительно даты отсчета
 *
 * @param {IDateRestriction} future
 * @param anchorDate
 * @returns {ValidationRule}
 */
export function getFutureRelativeRestrictionRule(
  future: IDateRestriction,
  anchorDate: MaybeRef<Date>
): ValidationRule {
  const days = countDaysDiff(future, anchorDate);
  const max = new Date(toValue(anchorDate));
  max.setDate(max.getDate() + days);
  if (future.direction === DateDirection.before) {
    return EarlierThenOrExact(max.toLocaleDateString());
  }
  return LaterThen(max.toLocaleDateString());
}
